import React, {
  useEffect, useLayoutEffect, lazy, Suspense,
} from 'react';
import {
  Navigate, Route, Routes, useLocation, useNavigate,
} from 'react-router-dom';
import './App.css';

import { Layout } from './components/layout/Layout';
import { getUserLanguage } from './utils/getUserLanguage';
import { useDispatch } from 'react-redux';
import { setInterfaceLanguage } from './state/slices/languageSlice';
import MoonLoader from 'react-spinners/MoonLoader';
import RentContract from './pages/rentContract/RentContract';
import { useAppSelector } from './state/hooks';
import { availableLanguages } from './constants/availableLanguages';
import Post from './pages/post/Post';
import { RingLoader } from 'react-spinners';
import RF15Course from './pages/courses/RF15Course/RF15Course';

const Main = lazy(() => import('./pages/main/Main'));
const Courses = lazy(() => import('./pages/courses/Courses'));
const WeaponCourse = lazy(() => import('./pages/courses/weaponCourse/WeaponCourse'));
const FirstAidCourse = lazy(() => import('./pages/courses/firstAidCourse/FirstAidCourse'));
const FireSafetyCourse = lazy(() => import('./pages/courses/fireSafetyCourse/FireSafetyCourse'));
const PersonalSafetyCourse = lazy(() => import('./pages/courses/personalSafetyCourse/PersonalSafetyCourse'));
const Rent = lazy(() => import('./pages/rent/Rent'));
const CorporateClients = lazy(() => import('./pages/corporateClients/CorporateClients'));
const About = lazy(() => import('./pages/about/About'));
const Contacts = lazy(() => import('./pages/contacts/Contacts'));
const Announces = lazy(() => import('./pages/announces/Announces'));
const Blog = lazy(() => import('./pages/blog/Blog'));
const Error = lazy(() => import('./pages/error/Error'));
const ThankYou = lazy(() => import('./pages/thank/ThankYou'));
const AKCourse = lazy(() => import('./pages/courses/akCourse/AKCourse'));
const PistolCourse = lazy(() => import('./pages/courses/pistolCourse/PistolCourse'));
const ShootingGallery = lazy(() => import('./pages/shootingGallery/ShootingGallery'));
const PrivacyPolicy = lazy(() => import('./pages/privacyPolicy/PrivacyPolicy'));
const GiftSertificates = lazy(() => import('./pages/giftSertificates/GiftSertificates'));
const MarchCourse = lazy(() => import('./pages/courses/marchCourse/MarchCourse'));

function App() {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { interfaceLanguage } = useAppSelector((state) => state.languages);
  const location = useLocation();

  useEffect(() => {
    window.scroll(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (location.search.length) {
      const goto = new URLSearchParams(location.search).get('goto');
      if (goto) {
        navigate(goto);
      }
    }
    const splittedPathName = pathname.split('/');

    if (!availableLanguages.includes(splittedPathName[1])) {
      // @ts-ignore
      dispatch(setInterfaceLanguage(getUserLanguage() || 'uk'));
    }
  }, []);

  useEffect(() => {
    const splittedPathName = pathname.split('/');

    if (splittedPathName[1] !== interfaceLanguage && availableLanguages.includes(splittedPathName[1])) {
      // @ts-ignore
      dispatch(setInterfaceLanguage(splittedPathName[1]));
    }
  }, [pathname]);

  return (
    <Suspense
      fallback={(
        <div className="spinner">
          <RingLoader
            color="#000"
            size={100}
          />
        </div>
      )}
    >
      <Routes>
        {availableLanguages.map((lang) => (
          <Route path={`/${lang}`} element={<Layout />}>
            <Route path="" element={<Main />} />

            <Route path="courses" element={<Courses />} />
            <Route path="courses/basic-course-in-handling-weapons" element={<WeaponCourse />} />
            <Route path="courses/basic-course-emergency-aid" element={<FirstAidCourse />} />
            <Route path="courses/basic-fire-safety-course" element={<FireSafetyCourse />} />
            <Route path="courses/basic-personal-safety-course" element={<PersonalSafetyCourse />} />
            <Route path="courses/ak-47-course" element={<AKCourse />} />
            <Route path="courses/pistol-course" element={<PistolCourse />} />
            <Route path="courses/march-course" element={<MarchCourse />} />
            <Route path="courses/rf15-course" element={<RF15Course />} />

            <Route path="shooting-gallery" element={<ShootingGallery />} />
            <Route path="gift-certificates" element={<GiftSertificates />} />

            <Route path="clients" element={<CorporateClients />} />
            <Route path="rent" element={<Rent />} />
            <Route path="about-us" element={<About />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="announces" element={<Announces />} />
            <Route path="announces/:slug" element={<Post />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/:slug" element={<Post />} />

            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="public-service-contract" element={<RentContract />} />
            <Route path="thank-you" element={<ThankYou />} />
            <Route path="error" element={<Error />} />
          </Route>
        ))}

        <Route path="/" element={<Navigate to={`/${interfaceLanguage}`} />} />
        <Route path="*" element={<Navigate to={`/${interfaceLanguage}/error`} />} />
      </Routes>
    </Suspense>
  );
}

export default App;
